import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import Loading from '../../../../../shared/loading';
import defaultOnErrorHandler from '../../../../../shared/default-on-error-handler';
import { TZ } from '../../../../../shared/consts';
import Tip from '../../../../../shared/tip';
import { HalfWrapper, SectionDetailLabel } from '../styles';
import MonthYearSelector from './month-year-selector';
import { recentCareerTip } from '../consts';
import { usePostUpdateRecentCareer, useDeleteRecentCareer } from '../../../../../services/profiles/contributor-settings-service';

const Container = styled.article`
  & > input {
    height: 40px;
  }
`;

const TipContainer = styled.div`
  border-bottom: 1px solid ${props => props.theme.secondaryColor30Transparent};
  margin-bottom: 30px;
`;

export default function RecentCareer({ recentCareer, onUpdate }) {
  const {
    joinedAt, leftAt, organisation, position,
  } = recentCareer || {
    joinedAt: null, leftAt: null, organisation: '', position: '',
  };
  const jmom = moment.tz(joinedAt, TZ);
  const lmom = moment.tz(leftAt, TZ);
  const [joined, setJoined] = React.useState(jmom);
  const [left, setLeft] = React.useState(lmom);
  const [company, setCompany] = React.useState(organisation);
  const [title, setTitle] = React.useState(position);
  const [{ loading, error }, post] = usePostUpdateRecentCareer();
  const [, deleteRecentCareer] = useDeleteRecentCareer();
  const loadingRef = React.useRef();

  React.useEffect(() => (loadingRef.current[loading ? 'start' : 'stop']()), [loading]);

  React.useEffect(() => {
    error && defaultOnErrorHandler(error, 'Error saving date of joining current role');
  }, [error]);

  /* eslint-disable complexity */
  useChangeAwareEffect(({ did }) => {
    if (!(did.joined.change || did.left.change || did.company.change || did.title.change)) {
      return;
    }
    const changed = company !== organisation || title !== position
      || (joined && joined.isValid() && !joined.isSame(jmom, 'month'))
      || (left && left.isValid() && !left.isSame(lmom, 'month'));
    // TODO: Proper validation and error message need to be implemented instead
    // of this silent one.
    const valid = _.get(company, 'length')
      && _.get(title, 'length')
      && joined
      && left
      && joined.isValid()
      && (!left.isValid() || joined.isSameOrBefore(left, 'month'));
    changed && valid && post({
      joinedAt: joined.format(),
      leftAt: left.isValid() ? left.format() : null,
      organisation: company,
      position: title,
    }).then(() => onUpdate({ profileCareers: { company, joinedAt: joined.format() } }));

    const inDeleteState = _.get(company, 'length') === 0
      && _.get(title, 'length') === 0
      && left === null
      && joined === null;

    if (inDeleteState) {
      deleteRecentCareer();
    }
  }, {
    joined, left, company, title, jmom, lmom, organisation, position,
  });
  /* eslint-enable complexity */

  const change = setter => e => setter(e.target.value);

  const datePair = [{
    ...(left && left.isValid() ? { max: left.format('MM-YYYY') } : {}),
    label: 'from',
    value: joinedAt,
    onDateSelected: setJoined,
    hasEmptyOption: true,
  }, {
    ...(joined && joined.isValid() ? { min: joined.format('MM-YYYY') } : {}),
    label: 'to',
    value: leftAt,
    onDateSelected: setLeft,
    hasEmptyOption: true,
  }];

  return (
    <>
      <Container>
        <SectionDetailLabel>company name</SectionDetailLabel>
        <input type="text" value={company} onChange={change(setCompany)} />
      </Container>
      <Container>
        <SectionDetailLabel>job title</SectionDetailLabel>
        <input type="text" value={title} onChange={change(setTitle)} />
      </Container>
      <HalfWrapper>
        {datePair.map(d => (
          <article key={d.label}>
            <SectionDetailLabel>{d.label}</SectionDetailLabel>
            <MonthYearSelector {...d} />
          </article>
        ))}
      </HalfWrapper>
      <Loading variant="sip" ref={loadingRef}>
        <TipContainer>
          <Tip text={recentCareerTip} />
        </TipContainer>
      </Loading>
    </>
  );
}

RecentCareer.propTypes = {
  recentCareer: PropTypes.shape({
    joinedAt: PropTypes.string.isRequired,
    leftAt: PropTypes.string,
    organisation: PropTypes.string.isRequired,
    position: PropTypes.string.isRequired,
  }),
  onUpdate: PropTypes.func,
};

RecentCareer.defaultProps = {
  recentCareer: null,
  onUpdate: () => null,
};
